import React, { useEffect, useState, useCallback, useRef } from 'react';
import { 
  Container, Paper, Typography, Box, Divider, Button, 
  Grid, Chip, List, ListItem, Stack
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ListIcon from '@mui/icons-material/List';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// --- 영상 주소 정규화 함수 ---
const getFullVideoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url; 
  const CLOUDFRONT_BASE = "https://d1nzw37nurloc4.cloudfront.net";
  return `${CLOUDFRONT_BASE}/${url}`;
};

const DetailVideoPlayer = ({ src, title }) => {
  const videoRef = useRef(null);
  const fullSrc = getFullVideoUrl(src); 
  if (!fullSrc) return null;

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
        <VideoLibraryIcon sx={{ fontSize: 14, mr: 0.5 }} /> {title || "연결된 영상"}
      </Typography>
      <Box 
        sx={{ 
          width: '100%', maxWidth: 480, borderRadius: 2, overflow: 'hidden', 
          backgroundColor: '#1a1a1a', border: '1px solid #ddd', aspectRatio: '16/9'
        }}
      >
        <video
          ref={videoRef}
          src={fullSrc}
          width="100%" height="100%"
          controls
          playsInline
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          style={{ objectFit: 'contain' }}
        />
      </Box>
    </Stack>
  );
};

const QuestionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [question, setQuestion] = useState(null);
  const THEME_NAVY = '#1a233a';

  const fetchQuestion = useCallback(async () => {
    try {
      const res = await axios.get(`/api/question/${id}`);
      let data = res.data;

      if (data.videoPk && !data.videoUrl) {
        try {
          const vRes = await axios.get(`/api/video/${data.videoPk}`);
          data.videoUrl = getFullVideoUrl(vRes.data.videoUrl);
          data.videoTitle = vRes.data.title;
        } catch (err) { console.error("대표 영상 조회 실패:", err); }
      } else {
        data.videoUrl = getFullVideoUrl(data.videoUrl);
      }

      if (data.options && data.options.length > 0) {
        data.options = await Promise.all(data.options.map(async (opt) => {
          if (opt.videoPk && !opt.videoUrl) {
            try {
              const ovRes = await axios.get(`/api/video/${opt.videoPk}`);
              return { ...opt, videoUrl: getFullVideoUrl(ovRes.data.videoUrl), videoTitle: ovRes.data.title };
            } catch { return opt; }
          }
          return { ...opt, videoUrl: getFullVideoUrl(opt.videoUrl) };
        }));
      }

      setQuestion(data);
    } catch (err) {
      console.error("데이터 로딩 실패:", err);
      alert("데이터를 불러올 수 없습니다.");
      navigate('/list');
    }
  }, [id, navigate]);

  useEffect(() => { fetchQuestion(); }, [fetchQuestion]);

  if (!question) return <Typography sx={{ p: 5, textAlign: 'center' }}>로딩 중...</Typography>;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/list')}>목록으로</Button>
        <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/edit/${id}`)} sx={{ backgroundColor: THEME_NAVY }}>수정하기</Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, borderTop: `6px solid ${THEME_NAVY}` }}>
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Chip label={question.categoryName || "미분류"} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
            {/* 배점 표시 추가 */}
            <Chip 
              icon={<CheckCircleOutlineIcon style={{ fontSize: '1rem' }} />} 
              label={`${question.score || 0}점`} 
              sx={{ fontWeight: 'bold', bgcolor: '#f0f4f8', color: THEME_NAVY, borderColor: '#d1d9e0' }} 
              variant="outlined"
            />
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800, color: THEME_NAVY }}>{question.title}</Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 5 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 2 }}><ListIcon sx={{ mr: 1 }} /> 문제 지문</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, mb: 3, border: '1px solid #eee' }}>{question.question}</Typography>
          <DetailVideoPlayer src={question.videoUrl} title={question.videoTitle} />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mb: 2 }}><CheckCircleIcon sx={{ mr: 1 }} /> 보기 및 정답 구성</Typography>
          <List disablePadding>
            {question.options?.map((opt, index) => (
              <ListItem key={index} sx={{ mb: 2, flexDirection: 'column', alignItems: 'flex-start', border: '1px solid #eee', borderRadius: 2, p: 2, bgcolor: opt.correctYn === 'Y' ? '#f0f7f0' : '#fff', borderLeft: opt.correctYn === 'Y' ? '6px solid #2e7d32' : '1px solid #eee' }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', mb: opt.videoUrl ? 2 : 0 }}>
                  {opt.correctYn === 'Y' ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color="disabled" />}
                  <Typography sx={{ fontWeight: opt.correctYn === 'Y' ? 'bold' : 'normal', flexGrow: 1 }}>{index + 1}. {opt.optionText}</Typography>
                  {opt.correctYn === 'Y' && <Chip label="정답" size="small" color="success" sx={{ fontWeight: 'bold' }} />}
                </Stack>
                {opt.videoUrl && <Box sx={{ pl: 5, width: '100%', mt: 1 }}><DetailVideoPlayer src={opt.videoUrl} title={opt.videoTitle} /></Box>}
              </ListItem>
            ))}
          </List>
        </Box>

        {question.description && (
          <Box sx={{ mt: 4, p: 2, bgcolor: '#fff9f2', borderRadius: 2, border: '1px solid #ffe0b2' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 1 }}>해설</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{question.description}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 4 }} />

        <Box sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ bgcolor: '#e9ecef', p: 1, borderRadius: '50%', display: 'flex' }}><HistoryIcon sx={{ fontSize: 18, color: '#6c757d' }} /></Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#adb5bd', display: 'block', fontWeight: 'bold' }}>최초 등록일</Typography>
                  <Typography variant="body2" sx={{ color: '#495057' }}><strong>{question.createUser || 'System'}</strong> <Typography component="span" variant="caption" sx={{ ml: 1, color: '#868e96' }}>({new Date(question.createDate).toLocaleString()})</Typography></Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ justifyContent: { md: 'flex-end' } }}>
                <Box sx={{ bgcolor: '#e9ecef', p: 1, borderRadius: '50%', display: 'flex' }}><EventNoteIcon sx={{ fontSize: 18, color: '#6c757d' }} /></Box>
                <Box sx={{ textAlign: { md: 'right' } }}>
                  <Typography variant="caption" sx={{ color: '#adb5bd', display: 'block', fontWeight: 'bold' }}>최종 수정일</Typography>
                  <Typography variant="body2" sx={{ color: '#495057' }}><strong>{question.updateUser || '-'}</strong> <Typography component="span" variant="caption" sx={{ ml: 1, color: '#868e96' }}>{question.updateDate ? `(${new Date(question.updateDate).toLocaleString()})` : '(수정 이력 없음)'}</Typography></Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default QuestionDetail;